import React from "react";
import { trackEngagement, ENGAGEMENT_PAGES } from "../api/reviews.js";

const HEARTBEAT_MS = 30000;
export const REVIEW_PROMPT_DELAY_MS = 120000;

/** Desktop-only floating feedback — above the shortlist FAB on the right. */
export const SOURCING_FEEDBACK_BUTTON_CLASS =
  "hidden md:inline-flex fixed z-40 items-center gap-2 rounded-full bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-sky-700 bottom-6 left-6";

/** Mobile: control in the sticky Filters/Sort bar — avoids covering product card rates. */
export const SOURCING_FEEDBACK_MOBILE_BAR_BUTTON_CLASS =
  "shrink-0 inline-flex items-center justify-center gap-1.5 rounded-xl border border-sky-500/30 bg-sky-600 px-3 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 sm:gap-2 sm:px-4 sm:text-sm";

function resolveEngagementPage(productCategory, pageOverride) {
  if (pageOverride) return pageOverride;
  return productCategory === "IF" ? ENGAGEMENT_PAGES.IF : ENGAGEMENT_PAGES.BTL;
}

function getScrollPercent() {
  try {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const height = (doc.scrollHeight || 0) - (window.innerHeight || 0);
    if (height <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((scrollTop / height) * 100)));
  } catch {
    return 0;
  }
}

/**
 * Fire engagement events on BTL / IF sourcing pages.
 * Uses dynesourcerapi.engagement_tracking.track_engagement with canonical `page` paths:
 *   BTL → /mortgages/buy-to-let-mortgage-calculator
 *   IF  → /invoice-finance/invoice-finance-sourcing
 */
export function useSourcingEngagement(productCategory, options = {}) {
  const category = productCategory === "IF" ? "IF" : "BTL";
  const page = resolveEngagementPage(category, options.page);
  const pageRef = React.useRef(page);
  const categoryRef = React.useRef(category);
  const startedAt = React.useRef(Date.now());
  const lastDwellSentAt = React.useRef(Date.now());
  const maxScroll = React.useRef(0);
  const flushed = React.useRef(false);
  const viewSent = React.useRef(false);

  pageRef.current = page;
  categoryRef.current = category;

  const currentScroll = React.useCallback(
    () => Math.max(maxScroll.current, getScrollPercent()),
    []
  );

  const elapsedSecondsSince = React.useCallback((fromMs) => {
    return Math.max(0, Math.round((Date.now() - fromMs) / 1000));
  }, []);

  const sendDwell = React.useCallback(
    (extra = {}) => {
      const seconds =
        extra.seconds != null
          ? Number(extra.seconds)
          : elapsedSecondsSince(lastDwellSentAt.current);
      if (!Number.isFinite(seconds) || seconds < 1) return false;

      lastDwellSentAt.current = Date.now();

      const payload = {
        page: pageRef.current,
        product_category: categoryRef.current,
        section: extra.section || "(whole page)",
        seconds,
        scroll_percent: extra.scroll_percent ?? currentScroll(),
        event_type: extra.event_type || undefined,
        product: extra.product || undefined,
      };

      if (extra.delivery === "beacon") {
        trackEngagement(payload, { delivery: "beacon" });
      } else {
        void trackEngagement(payload);
      }
      return true;
    },
    [currentScroll, elapsedSecondsSince]
  );

  const flushDwell = React.useCallback(
    (extra = {}) => {
      if (flushed.current) return;
      flushed.current = true;
      sendDwell({
        ...extra,
        delivery: extra.delivery || "beacon",
        event_type: extra.event_type || "page_exit",
      });
    },
    [sendDwell]
  );

  React.useEffect(() => {
    startedAt.current = Date.now();
    lastDwellSentAt.current = Date.now();
    flushed.current = false;
    maxScroll.current = getScrollPercent();

    if (!viewSent.current) {
      viewSent.current = true;
      void trackEngagement({
        page,
        product_category: category,
        event_type: "page_view",
      });
    }

    const onScroll = () => {
      maxScroll.current = Math.max(maxScroll.current, getScrollPercent());
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendDwell({ delivery: "beacon", event_type: "visibility_hidden" });
        return;
      }
      if (document.visibilityState === "visible") {
        lastDwellSentAt.current = Date.now();
      }
    };

    const onPageHide = () => {
      flushDwell();
    };

    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      sendDwell({ event_type: "heartbeat" });
    }, HEARTBEAT_MS);

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      flushDwell();
    };
  }, [page, category, flushDwell, sendDwell]);

  const track = React.useCallback(
    (eventType, extra = {}) => {
      const section =
        extra.section ||
        (eventType === "product_view" ? "product_detail" : eventType) ||
        undefined;
      const seconds =
        extra.seconds != null
          ? Number(extra.seconds)
          : Math.max(1, elapsedSecondsSince(startedAt.current));

      void trackEngagement({
        page,
        product_category: category,
        section,
        product: extra.product,
        seconds,
        scroll_percent: extra.scroll_percent ?? currentScroll(),
        event_type: eventType,
      });
    },
    [page, category, currentScroll, elapsedSecondsSince]
  );

  return { track, page };
}

const REVIEW_DISMISS_PREFIX = "ds_review_prompt_dismissed_";

const INTERACTIVE_CONTROL_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="option"], [role="switch"], [role="checkbox"], [role="radio"], [role="tab"], [role="combobox"]';
const AFTER_INTERACTION_DELAY_MS = 3000;

function isPageControlInteraction(target) {
  if (!(target instanceof Element)) return false;
  if (!target.closest(INTERACTIVE_CONTROL_SELECTOR)) return false;
  // Dismissing pop-ups (sign-up, validation, review) is not engagement with the page.
  if (target.closest('[role="dialog"], [aria-modal="true"]')) return false;
  return true;
}

/**
 * @param {boolean} [requireInteraction] Only prompt once the user has used a page
 *   control (button, input, dropdown…); the prompt then waits until `delayMs`
 *   since load has passed, and at least a few seconds after that interaction.
 */
export function useReviewPrompt({
  storageKey,
  delayMs = REVIEW_PROMPT_DELAY_MS,
  enabled = true,
  requireInteraction = false,
} = {}) {
  const [open, setOpen] = React.useState(false);
  const key = `${REVIEW_DISMISS_PREFIX}${storageKey || "default"}`;

  React.useEffect(() => {
    if (!enabled) return undefined;
    try {
      if (sessionStorage.getItem(key) === "1") return undefined;
    } catch {
      /* ignore */
    }

    if (!requireInteraction) {
      const t = window.setTimeout(() => setOpen(true), delayMs);
      return () => window.clearTimeout(t);
    }

    const loadedAt = Date.now();
    let timer = null;
    const onInteraction = (e) => {
      if (timer || !isPageControlInteraction(e.target)) return;
      const wait = Math.max(delayMs - (Date.now() - loadedAt), AFTER_INTERACTION_DELAY_MS);
      timer = window.setTimeout(() => setOpen(true), wait);
      document.removeEventListener("click", onInteraction, true);
      document.removeEventListener("change", onInteraction, true);
      document.removeEventListener("input", onInteraction, true);
    };
    document.addEventListener("click", onInteraction, true);
    document.addEventListener("change", onInteraction, true);
    document.addEventListener("input", onInteraction, true);
    return () => {
      document.removeEventListener("click", onInteraction, true);
      document.removeEventListener("change", onInteraction, true);
      document.removeEventListener("input", onInteraction, true);
      if (timer) window.clearTimeout(timer);
    };
  }, [enabled, delayMs, key, requireInteraction]);

  const dismiss = React.useCallback(() => {
    setOpen(false);
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
  }, [key]);

  const openNow = React.useCallback(() => setOpen(true), []);

  return { open, setOpen, dismiss, openNow };
}
